import React, { Suspense, useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useNavigationType, ScrollRestoration } from 'react-router';
import { useLenis } from 'lenis/react';
import { Toaster } from './components/ui/sonner';
import { useAuth } from '../hooks/useAuth';
import { AUTH_ROUTES } from '../constants/routes';
import { titleForPath } from '../lib/pageTitles';
import { ScrollReveals } from '../lib/animation/ScrollReveals';

// Brand loader for the initial session check.
function FullScreenSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--surface)' }}>
      <div className="mtt-loader" role="status" aria-label="Loading" />
    </div>
  );
}

// Slim top progress bar shown only if a screen's code is still downloading. Chunks are
// prefetched after first paint, so in practice this is rarely visible.
function RouteProgress() {
  return <div className="mtt-route-progress" role="progressbar" aria-label="Loading page" />;
}

export function Root() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const lenis = useLenis();
  const path = location.pathname;

  const isAuth = (AUTH_ROUTES as readonly string[]).includes(path);

  // Keep the browser-tab title in sync with the current page (Gmail-style).
  useEffect(() => {
    document.title = titleForPath(path);
  }, [path]);

  // New pages start at the top instantly; back/forward keeps ScrollRestoration's position.
  // Stopping any in-flight smooth scroll prevents it carrying over onto the next screen.
  useEffect(() => {
    if (navigationType !== 'POP') {
      lenis?.scrollTo(0, { immediate: true, force: true });
    }
  }, [path, navigationType, lenis]);

  useEffect(() => {
    if (!loading && user && isAuth) {
      navigate('/home', { replace: true });
    }
  }, [user, loading, isAuth, navigate]);

  if (loading) {
    return <FullScreenSpinner />;
  }

  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: 'var(--font-ui)' }}>
      <ScrollRestoration />
      <ScrollReveals />
      <Suspense fallback={<RouteProgress />}>
        <Outlet />
      </Suspense>
      <Toaster />
    </div>
  );
}
